import React, { useEffect } from 'react';
import { Copy, Trash2, Plus } from 'lucide-react';
import { LineItem } from './invoiceType';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';

interface CreateItemizedInvoiceProps {
  lineItems: LineItem[];
  setLineItems: React.Dispatch<React.SetStateAction<LineItem[]>>;
  onTotalsChange: (totals: {
    dollars: number;
  }) => void;
}

const CreateItemizedInvoice = ({ lineItems, setLineItems, onTotalsChange }: CreateItemizedInvoiceProps) => {
  const updateTotals = (items: LineItem[]) => {
    const totalDollars = items.reduce((sum, item) => sum + (item.amount_dollar || 0), 0);
    onTotalsChange({ dollars: totalDollars });
  };

  // Call updateTotals whenever lineItems change
  useEffect(() => {
    updateTotals(lineItems);
  }, [lineItems, onTotalsChange]);

  const handleLineItemChange = (id: number, field: string, value: string | number) => {
    setLineItems(prevItems => {
      const newItems = prevItems.map(item => {
        if (item.id === id) {
          const updatedItem = { ...item, [field]: value };
          return updatedItem;
        }
        return item;
      });

      return newItems;
    });
  };

  const addNewLine = () => {
    const newItem: LineItem = {
      id: Date.now(),
      code: '',
      description: '',
      amount_dollar: 0,
      invoice_id: '',
    };
    setLineItems(prevItems => [...prevItems, newItem]);
  };

  const copyLine = (id: number) => {
    const itemToCopy = lineItems.find(item => item.id === id);
    if (itemToCopy) {
      setLineItems(prevItems => [...prevItems, { ...itemToCopy, id: Date.now() }]);
    }
  };

  const deleteLine = (id: number) => {
    if (lineItems.length > 1) {
      setLineItems(prevItems => prevItems.filter(item => item.id !== id));
    }
  };

  return (
    <div className="space-y-3">
      {lineItems.map((item, index) => {
        const backgroundColor = index % 2 === 0 ? 'bg-white' : 'bg-gray-50';

        return (
          <div
            key={item.id}
            className={`${backgroundColor} border border-gray-300 rounded-lg overflow-hidden`}
          >
            {/* Header Row - Code + Actions */}
            <div className="flex items-center justify-between gap-4 px-4 py-3 bg-gray-100 border-b border-gray-300">
              <div className="flex-1">
                <Label htmlFor={`code-${item.id}`} className="mb-1">
                  Code <span className="text-destructive">*</span>
                </Label>
                <Input
                  id={`code-${item.id}`}
                  placeholder="Enter Code"
                  value={item.code}
                  onChange={(e) => handleLineItemChange(item.id, 'code', e.target.value)}
                  required
                  className="max-w-md bg-white"
                />
              </div>
              
              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => copyLine(item.id)}
                  className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                  title="Copy Item"
                >
                  <Copy size={18} className="text-primary" />
                </button>
                {lineItems.length > 1 && (
                  <button
                    type="button"
                    onClick={() => deleteLine(item.id)}
                    className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                    title="Delete Item"
                  >
                    <Trash2 size={18} className="text-destructive" />
                  </button>
                )}
              </div>
            </div>

            {/* Description Section */}
            <div className="px-4 py-3 border-b border-gray-200">
              <Label htmlFor={`description-${item.id}`} className="mb-1">
                Description <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id={`description-${item.id}`}
                placeholder="Enter description"
                value={item.description}
                onChange={(e) => handleLineItemChange(item.id, 'description', e.target.value)}
                rows={2}
                required
                className="resize-none"
              />
            </div>

            {/* Amount Section */}
            <div className="px-4 py-3">
              <div className="flex items-end gap-3">
                <div className="flex-1 max-w-xs">
                  <Label htmlFor={`amount-${item.id}`} className="mb-1">
                    Amount ($) <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id={`amount-${item.id}`}
                    type="number"
                    min={0}
                    step={0.01}
                    placeholder="Enter Amount"
                    value={item.amount_dollar === 0 ? '' : item.amount_dollar}
                    onChange={(e) =>
                      handleLineItemChange(item.id, 'amount_dollar', parseFloat(e.target.value) || 0)
                    }
                    required
                  />
                </div>
              </div>
            </div>
          </div>
        );
      })}

      {/* Add New Line Button */}
      <div className="pt-2">
        <Button type="button" size="medium" variant="inverted" onClick={addNewLine}>
          <Plus size={20} />
          Add Itemized Invoice
        </Button>
      </div>
    </div>
  );
};

export default CreateItemizedInvoice;